export const getUnsupportedMediaFormatMessage = (kind) => {
  if (kind === "image") {
    return "This photo format isn’t supported. Supported formats: JPG, JPEG, PNG or GIF. Choose a different photo."
  }

  return "This video format isn’t supported. Supported formats: MP4, MOV or WebM. Choose a different video."
}

export const getMediaTooLargeMessage = (kind) => {
  if (kind === "image") {
    return "This photo is too large. Photos can be up to 10 MB. Choose a smaller photo and try again."
  }

  return "This video is too large. Videos can be up to 100 MB. Choose a smaller video and try again."
}

export const getVideoDurationMessage = (maxDurationSec) =>
  `This video is longer than the approximate ${maxDurationSec} second limit. Choose a shorter video and try again.`

export const getMediaSelectionError = ({
  file,
  kind,
  allowedTypes,
  maxSizeBytes,
}) => {
  if (!file) return null

  const fileType = file.type || ""
  const hasTypeRestrictions =
    Array.isArray(allowedTypes) && allowedTypes.length > 0
  const isAllowedType =
    !hasTypeRestrictions ||
    (fileType &&
      allowedTypes.some(
        (allowedType) =>
          (allowedType.endsWith("/*") &&
            fileType.startsWith(allowedType.slice(0, -1))) ||
          fileType === allowedType ||
          fileType.startsWith(`${allowedType};`)
      ))

  if (!isAllowedType) {
    return getUnsupportedMediaFormatMessage(kind)
  }

  if (maxSizeBytes && file.size > maxSizeBytes) {
    return getMediaTooLargeMessage(kind)
  }

  return null
}

const findResponse = (error) => {
  let current = error

  for (let depth = 0; depth < 5 && current; depth += 1) {
    if (current.response) return current.response
    current = current.cause
  }

  return null
}

const hasNetworkFailure = (error) => {
  let current = error

  for (let depth = 0; depth < 5 && current; depth += 1) {
    if (
      !current.response &&
      (current.request ||
        current.code === "ERR_NETWORK" ||
        current.message === "Network Error" ||
        current.message === "Network connectivity issue")
    ) {
      return true
    }

    current = current.cause
  }

  return false
}

const validationLabels = {
  name: "Name", instructions: "Instructions", image: "Photo", video: "Video",
  program: "Program", program_id: "Program", client_id: "Client",
  start_date: "Start date", end_date: "End date", days_of_week: "Days",
  non_field_errors: "",
}

const validationMessage = (data) => {
  if (typeof data?.detail === "string") return data.detail
  if (!data || typeof data !== "object") return null
  const messages = Object.entries(validationLabels).flatMap(([field, label]) => {
    const value = data[field]
    const items = Array.isArray(value) ? value : [value]
    return items.filter((item) => typeof item === "string")
      .map((item) => label ? `${label}: ${item}` : item)
  })
  return messages.length ? messages.join(" ") : null
}

export const getUploadErrorMessage = (error, context = "form submission") => {
  const response = findResponse(error)

  if (!response && hasNetworkFailure(error)) {
    return "We couldn’t upload this media because the connection was interrupted. Your changes are still here. Check your connection and try saving or selecting Send to client again."
  }

  if (response?.status === 413) {
    return "One or more media files are too large. Photos can be up to 10 MB and videos can be up to 100 MB. Choose a smaller file and try again."
  }

  if (response?.status >= 500) {
    return "We couldn’t upload this media right now. Please try again. If it keeps happening, contact support."
  }

  if (response?.status === 400) {
    return (
      response.data?.user_message ||
      response.data?.error_message ||
      response.data?.message ||
      validationMessage(response.data) ||
      `Invalid data provided for ${context}. Please check all fields and try again.`
    )
  }

  if (response?.status === 401) {
    return "Your session has expired. Please log in again."
  }

  if (response?.status === 403) {
    return "You don’t have permission to perform this action. Please contact your administrator."
  }

  if (response?.status === 404) {
    return `Required resource not found for ${context}. Please refresh and try again.`
  }

  if (response?.status === 409) {
    return `A conflict occurred during ${context}. The data may have been modified by another user.`
  }

  if (response?.data?.user_message) {
    return response.data.user_message
  }

  if (response?.data?.error_message) {
    return response.data.error_message
  }

  if (error?.message) {
    return error.message
  }

  return `An unexpected error occurred during ${context}. Please try again or contact support.`
}
